// @flow
import * as React from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  Form, TextArea, Checkbox,
} from 'informed';
import moment from 'moment';

import {
  updateWellChildCheck,
} from '../../../../actions/wellChildCheckActions';

import ContentBox from '../../../ContentBox.jsx';
import { PrimaryText, Label } from '../../../Typography.jsx';
import { Button, PrimaryButton } from '../../../Buttons.jsx';
import DateText from '../../../DateText.jsx';

import EditVitals from './EditVitals.jsx';
import EditPhysicalExamination from './EditPhysicalExamination.jsx';
import EditDevelopment from './EditDevelopment.jsx';

import examItems from '../physicalExaminationItems';

import styles from '../../../../styles/patientPages/wellChildCheck/Form.scss';

type Props = {
  activePatientId: string,
  wellChildChecks: Object[],
  activeAccount: Object,
  updateWellChildCheck: Function,
  match: { params: { id: string, type: number } },
}

type State = {
  wellChildCheck: Object,
}

class EditFormPageComponent extends React.Component<Props, State> {
  formApi: {
    getState: Function,
    setTouched: Function,
    setValue: Function,
  }

  constructor(props: Props) {
    super(props);
    this.state = {
      wellChildCheck: this.props.wellChildChecks.find(
        wcc => wcc._id === this.props.match.params.id,
      ) || {},
      override: false,
    };
  }

  setFormApi = (formApi: Object) => {
    this.formApi = formApi;
    formApi.setValues(this.state.wellChildCheck);
  }

  // function to set all physical exam items to normal
  setAllNormal = () => {
    for (let i = 0; i < examItems.length; i++) {
      this.formApi.setValue(`physicalExamination[${i}].normal`, true);
    }
    this.formApi.setTouched('physicalExamination[0].normal', true);
  }

  // function to set all physical exam items to normal
  resetAll = () => {
    for (let i = 0; i < examItems.length; i++) {
      this.formApi.setValue(`physicalExamination[${i}].normal`, null);
    }
    this.formApi.setTouched('physicalExamination[0].normal', true);
  }

  handleSave = (e) => {
    const state = this.formApi.getState();
    if (state.invalid) {
      e.preventDefault();
      return;
    }
    if (!Object.keys(state.touched).length) {
      return;
    }
    const formValues = state.values;

    // add active account for edit history
    const { activeAccount } = this.props;
    formValues.editor = `${activeAccount.name}, ${activeAccount.credential}`;
    formValues.editDate = moment().toISOString();

    // transform date
    formValues.date = moment(formValues.date, 'DD-MM-YYYY').toISOString();

    // transform measurements if necessary b/w units
    const {
      weightUnit, lengthUnit, temperatureUnit, headCircumferenceUnit, ...rest
    } = formValues;
    if (weightUnit === 'lb') {
      rest.vitals.weight = (rest.vitals.weight / 2.2).toFixed(1);
    }
    if (lengthUnit === 'in') {
      rest.vitals.length = (rest.vitals.length / 0.3937).toFixed(1);
    }
    if (headCircumferenceUnit === 'in') {
      rest.vitals.headCircumference = (rest.vitals.headCircumference / 0.3937).toFixed(1);
    }
    if (temperatureUnit === 'c') {
      rest.vitals.temperature = (rest.vitals.temperature * 9 / 5 + 32).toFixed(1);
    }
    this.props.updateWellChildCheck(this.props.activePatientId, {
      wccId: this.props.match.params.id,
      ...rest,
    });
  }

  render() {
    const {
      vitals,
      physicalExamination,
      developmentMilestones,
      developmentNormal,
      developmentNotes,
      developmentGrade,
      notes,
      editors,
      editDates,
      overridden,
      age,
    } = this.state.wellChildCheck;

    let { type } = this.props.match.params;
    type = parseInt(type, 10);

    let editHistory = '';
    for (let i = 0; i < editors.length; i++) {
      if (i === editors.length - 1) {
        editHistory += `${moment(editDates[i]).format('DD-MM-YYYY, HH:mm')} - ${editors[i]} (created) \n`;
      } else {
        editHistory += `${moment(editDates[i]).format('DD-MM-YYYY, HH:mm')} - ${editors[i]}\n`;
      }
    }

    return (
      <Form getApi={this.setFormApi} autoComplete='off'>
        <ContentBox title={`${age} Well Child Check`}>
          <table>
            <tbody>
              <tr>
                <td><Checkbox field='overridden' initialValue={overridden} /></td>
                <td><Label>Override WCC Requirement</Label></td>
              </tr>
            </tbody>
          </table>
          <div className={styles.topRow}>
            <div className={styles.vitals}>
              <EditVitals
                vitals={vitals}
                type={type}
              />
            </div>
            <div className={styles.development}>
              <EditDevelopment
                developmentMilestones={developmentMilestones}
                developmentNormal={developmentNormal}
                developmentNotes={developmentNotes}
                developmentGrade={developmentGrade}
                type={type}
              />
            </div>
          </div>
          <div className={styles.physicalExam}>
            <EditPhysicalExamination
              physicalExamination={physicalExamination}
              setAllNormal={this.setAllNormal}
              resetAll={this.resetAll}
            />
          </div>
          <div className={styles.notes}>
            <PrimaryText>Notes</PrimaryText>
            <TextArea field='notes' initialValue={notes} />
          </div>
          <table className={styles.examDateTable}>
            <tbody>
              <tr>
                <td><PrimaryText>Exam Date: </PrimaryText></td>
                <td><DateText field='date' item={this.state.wellChildCheck} /></td>
              </tr>
              <tr className={styles.editHistoryRow}>
                <td><strong>Edit History: </strong></td>
                <td>{editHistory || '--'}</td>
              </tr>
            </tbody>
          </table>
          {/* Slow if use history API to go back, for some reason */}
          <Link to='../../../wellChildChecks' onClick={this.handleSave}>
            <PrimaryButton>Save</PrimaryButton>
          </Link>
          <Link to='../../../wellChildChecks'>
            <Button>Cancel</Button>
          </Link>
        </ContentBox>
      </Form>
    );
  }
}

const mapStateToProps = state => ({
  activePatientId: state.patient.data.activePatientId,
  wellChildChecks: state.patient.data.wellChildChecks,
  activeAccount: state.account.activeAccount,
});

const mapDispatchToProps = dispatch => ({
  updateWellChildCheck: (patientId, wcc) => dispatch(updateWellChildCheck(patientId, wcc)),
});

const EditFormPage = connect(
  mapStateToProps,
  mapDispatchToProps,
)(EditFormPageComponent);

export default EditFormPage;



// WEBPACK FOOTER //
// ./client/src/components/patientPages/wellChildCheck/edit/EditFormPage.jsx